// Tarea 2: Utilidades para theme
import { ThemeTokens } from '@/types/theme';
import { generateGoogleFontsUrl, getHeadingTopOffset, getHomeHeadingOpticalOffset, GOOGLE_FONTS } from './googleFonts';
import { getDefaultThemeTokens } from './themeResolver';

/**
 * Merge ThemeTokens with deep merge for nested objects
 */
export function mergeTokens(base: ThemeTokens, overrides: ThemeTokens): ThemeTokens {
  return {
    colors: {
      ...base.colors,
      ...overrides.colors,
    },
    fonts: {
      ...base.fonts,
      ...overrides.fonts,
    }
  };
}

/**
 * Convert legacy ThemeColors to new ThemeTokens format
 */
export function convertLegacyColors(colors: Record<string, string>): ThemeTokens {
  return {
    colors: colors as ThemeTokens['colors']
  };
}

/**
 * Apply tokens to CSS variables and inject Google Fonts if needed
 */
export function applyTokensToDOM(tokens: ThemeTokens): void {
  document.documentElement.style.setProperty('--home-h1-optical-shift-y', '0em');
  document.documentElement.style.setProperty('--heading-top-offset', '0px');

  // Apply color variables
  if (tokens.colors) {
    Object.entries(tokens.colors).forEach(([key, value]) => {
      if (value) {
        document.documentElement.style.setProperty(`--${key}`, value);
      }
    });
  }

  // Apply font variables and load Google Fonts
  if (tokens.fonts) {
    const googleFontNames: string[] = [];

    if (tokens.fonts.general) {
      document.documentElement.style.setProperty('--font-sans', tokens.fonts.general);
      // Extract Google Font name from font family string
      const generalFontName = extractGoogleFontName(tokens.fonts.general);
      if (generalFontName) googleFontNames.push(generalFontName);
    }

    if (tokens.fonts.headings) {
      document.documentElement.style.setProperty('--font-heading', tokens.fonts.headings);
      document.documentElement.style.setProperty(
        '--heading-top-offset',
        getHeadingTopOffset(tokens.fonts.headings),
      );
      document.documentElement.style.setProperty(
        '--home-h1-optical-shift-y',
        getHomeHeadingOpticalOffset(tokens.fonts.headings),
      );
      // Extract Google Font name from font family string
      const headingFontName = extractGoogleFontName(tokens.fonts.headings);
      if (headingFontName) googleFontNames.push(headingFontName);
    }

    // Load Google Fonts if any were found
    if (googleFontNames.length > 0) {
      const uniqueFontNames = [...new Set(googleFontNames)];
      const googleFontsUrl = generateGoogleFontsUrl(uniqueFontNames);
      if (googleFontsUrl) {
        injectGoogleFonts(googleFontsUrl);
      }
    }
  }
}

/**
 * Extract Google Font name from a font family string
 * e.g., "Source Code Pro, ui-monospace, monospace" -> "Source Code Pro"
 */
function extractGoogleFontName(fontFamily: string): string | null {
  const fontName = fontFamily.split(',')[0].trim().replace(/['"]/g, '');

  // Check if this font exists in our Google Fonts list
  const foundFont = GOOGLE_FONTS.find(font => font.name === fontName);
  return foundFont ? fontName : null;
}

/**
 * Inject Google Fonts link if it doesn't exist already
 */
function injectGoogleFonts(url: string): void {
  const existingLink = document.querySelector(`link[href="${url}"]`);
  if (!existingLink) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.id = 'google-fonts-theme';

    // Remove existing theme font links
    const oldLink = document.getElementById('google-fonts-theme');
    if (oldLink) {
      oldLink.remove();
    }

    document.head.appendChild(link);
  }
}

/**
 * Convert HSL color string to luminance value (0-1)
 * HSL format: "h s% l%"
 */
function hslToLuminance(hslString: string): number {
  try {
    const parts = hslString.split(' ');
    if (parts.length < 3) return 0.5; // Default middle gray
    // Extract lightness percentage (3rd part like "84%")
    const lightnessStr = parts[2].replace('%', '');
    const lightness = Math.max(0, Math.min(100, parseFloat(lightnessStr))) / 100;
    return lightness;
  } catch {
    return 0.5; // Default to middle gray on error
  }
}

/**
 * Determine if theme is dark based on background luminance
 * A theme is considered dark if the background lightness is below 50%
 */
export function isDarkTheme(tokens: ThemeTokens): boolean {
  try {
    const bgColor = tokens.colors?.background;
    if (!bgColor) return false; // Default to light if no background color
    const luminance = hslToLuminance(bgColor);
    return luminance < 0.5;
  } catch {
    return false; // Default to light on any error
  }
}

/**
 * Get default theme tokens based on current CSS variables (BloomKeep branding)
 */
export function getDefaultTokens(): ThemeTokens {
  return getDefaultThemeTokens();
}
